import clsx from 'clsx'
import PropTypes from 'prop-types'
import React, { useEffect } from 'react'
import { Modal as BootstrapModal } from 'react-bootstrap'

const propTypes = {
  /**
   * If true, modal is shown.
   */
  show: PropTypes.bool,
  /**
   * Callback to hide the modal
   */
  onHide: PropTypes.func.isRequired,
  /**
   * Modal content/panels
   */
  children: PropTypes.node.isRequired,
  /**
   * Size of the modal
   */
  size: PropTypes.string,
  /**
   * If true, puts modal behind the backdrop, in case of another modal is opened on top of it for instance
   */
  toBackdrop: PropTypes.bool,
}

// Nested sidebars (a picker opened from an already-open panel) each mount this
// hook. Only the outermost lock may read scrollY and pin body — an inner one
// would see scrollY 0 and, on close, jump the page to the top.
let documentScrollLockCount = 0
let documentScrollLockY = 0
let documentScrollLockPreviousStyle = null

function lockDocumentScroll() {
  documentScrollLockCount += 1
  if (documentScrollLockCount > 1) {
    return
  }

  documentScrollLockY = window.scrollY
  const { style } = document.body
  documentScrollLockPreviousStyle = {
    position: style.position,
    top: style.top,
    left: style.left,
    right: style.right,
    width: style.width,
  }

  style.position = 'fixed'
  style.top = `-${documentScrollLockY}px`
  style.left = '0'
  style.right = '0'
  style.width = '100%'
}

function unlockDocumentScroll() {
  documentScrollLockCount = Math.max(0, documentScrollLockCount - 1)
  if (documentScrollLockCount > 0 || !documentScrollLockPreviousStyle) {
    return
  }

  const { style } = document.body
  const previous = documentScrollLockPreviousStyle
  documentScrollLockPreviousStyle = null
  style.position = previous.position
  style.top = previous.top
  style.left = previous.left
  style.right = previous.right
  style.width = previous.width
  window.scrollTo(0, documentScrollLockY)
}

/**
 * Bootstrap hides overflow on `body`, but this app scrolls on the document
 * element. Pin `body` at the current scroll offset so a wheel over the
 * sidebar — or a select menu portaled onto `body` — cannot move the page
 * behind the panel, and closing the panel returns to the same place.
 */
function useLockDocumentScroll(locked) {
  useEffect(() => {
    if (!locked) {
      return undefined
    }

    lockDocumentScroll()
    return unlockDocumentScroll
  }, [locked])
}

/**
 * Wrapper of the bootstrap modal for displaying it in as a sidebar
 */
function Modal(props) {
  const { show = false, onHide, children, size = 'md', toBackdrop = false } = props ?? {}

  useLockDocumentScroll(show)

  return (
    <BootstrapModal
      backdrop
      size={size}
      show={show}
      onHide={onHide}
      className={clsx('right', {
        in: show,
        'modal-behind-backdrop': toBackdrop,
      })}
    >
      {children}
    </BootstrapModal>
  )
}

Modal.propTypes = propTypes

export default Modal
